import React, { useState, useMemo, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { useNotification } from '../../../../../../contexts/NotificationsContext';
import SearchBar from '../../../../../components/SearchBar';
import Card from '../../../../../../muiComponents/Card';
import Title from '../../../Title';
import useTranslate from '../../../../../../customHooks/useTranslate';
import Button from '../../../../../../muiComponents/Button';
import {
    usePropertyFetching,
    usePropertyPresentation,
    usePropertyRoomDetail,
} from '../../../../../contextsProperty/ContextProperties';
import RoomItem from './RoomItem';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';
import {
    DDBB_NEW_ROOM_INITIAL_STATE,
    ROOM_INITIAL_STATE,
    URL_HOTEL_BASE,
    URL_INVENTORY_LIST,
    URL_TAB_PARAM,
} from '../../../../../../utils/property/propertyUtils';
import convertToOvariant from '../../../../../../utils/convertToOvariant';
import { savableEntities } from '../../../../../../utils/commonVariables/entityKeys';
import api from '../../../../../../services/api';
import endpoints from '../../../../../../utils/commonVariables/endpoints';
import useUserProfile from '../../../../../../customHooks/useUserProfile';
import encodeBase64 from '../../../../../../utils/encodeBase64';

const RoomList = () => {
    const [searchValue, setSearchValue] = useState('');
    const { readOnlyUser } = useUserProfile();
    const { rooms, idSupplierHotel } = usePropertyPresentation();
    const { handleFetching, isFetching } = usePropertyFetching();
    const { addNewRoom, handleTargetRoom } = usePropertyRoomDetail();
    // old code => refactor in new context
    const { setErrorMessage } = useNotification();

    const history = useHistory();
    const buttonText = useTranslate('add_room');
    const searcherPlaceholder = useTranslate('search_room');
    const headerRoom = useTranslate('room');
    const headerServices = useTranslate('services');
    const headerBedType = useTranslate('bed_types');
    const noResultsText = useTranslate('no_results');

    const filteredRooms = useMemo(() => {
        return rooms.filter(
            room =>
                room.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                room.description
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
        );
    }, [rooms, searchValue]);

    async function handleCreateNewRoom() {
        handleFetching(true);
        const newRoom = DDBB_NEW_ROOM_INITIAL_STATE;
        newRoom.IdSupplierHotel = `${idSupplierHotel}`;
        const objectToSend = convertToOvariant(
            savableEntities.SupplierHotelRoomType,
            newRoom,
            '-1'
        );
        try {
            const responseSave = await api.post(
                endpoints.commonSave,
                objectToSend
            );

            const newRoomState = {
                ...ROOM_INITIAL_STATE,
                id: responseSave.data.Id,
                name: responseSave.data.Name,
            };
            addNewRoom(newRoomState);
            handleTargetRoom(newRoomState.id);
            history.push(
                `${URL_HOTEL_BASE(
                    idSupplierHotel
                )}${URL_INVENTORY_LIST}/${encodeBase64(
                    responseSave.data.Id
                )}${URL_TAB_PARAM(1)}`
            );
        } catch (error) {
            setErrorMessage('something_went_wrong');
        } finally {
            handleFetching(false);
        }
    }

    return (
        <section className='flex-column gap-10'>
            <div className='flex flex-between'>
                {isFetching ? (
                    <LoadingPlaceholder
                        variant='text'
                        height={24}
                        width='100%'
                    />
                ) : (
                    <Title text='room_configuration' />
                )}
            </div>

            <div className='flex gap-20 flex-align-center'>
                {isFetching ? (
                    <LoadingPlaceholder
                        variant='text'
                        height={32}
                        width='100%'
                    />
                ) : (
                    <>
                        <SearchBar
                            value={searchValue}
                            placeholder={searcherPlaceholder}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setSearchValue(e?.target?.value || '')
                            }
                            onClear={() => setSearchValue('')}
                        />
                        <Button
                            variant='contained'
                            className='white-space-nowrap'
                            style={{ height: '30px' }}
                            onClick={handleCreateNewRoom}
                            disabled={readOnlyUser}
                        >
                            {buttonText}
                        </Button>
                    </>
                )}
            </div>
            <Card className='padding20 overflow-x-auto'>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell style={{ textTransform: 'capitalize' }}>
                                {headerRoom}
                            </TableCell>
                            <TableCell>{headerServices}</TableCell>
                            <TableCell>{headerBedType}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRooms.map(room => (
                            <RoomItem key={room.id} room={room} />
                        ))}
                        {!filteredRooms.length && !isFetching && (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    style={{ textAlign: 'center' }}
                                >
                                    {noResultsText}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>
        </section>
    );
};

export default RoomList;
